import React, { useState } from 'react';
import { Typography, Card, Button, Input, Space, Row, Col, message, Divider } from 'antd';
import { CopyOutlined, GithubOutlined } from '@ant-design/icons';
import { RefLanguage } from '../hooks/LanguageHooks';
import { RefStoredValue } from '../hooks/StoreHooks';
import { BackButton } from '../elements/CustomButtons';
import * as con from '../Constants';

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;

function BugReportPage() {
    const i18n = RefLanguage();
    
    // Get system information from store
    const selectedDatabase = RefStoredValue(con.SELECTED_DATABASE);
    const currentFileId = RefStoredValue(con.CURRENT_FILE_ID);
    const currentJobId = RefStoredValue(con.CURRENT_JOB_ID);
    const lastAnnotationMethod = RefStoredValue(con.SELECTED_METHOD);
    const currentLocale = RefStoredValue(con.STORAGE_CURRENT_LOCAL);
    
    // Template state
    const [stepsToReproduce, setStepsToReproduce] = useState('');
    const [expectedBehavior, setExpectedBehavior] = useState('');
    const [actualBehavior, setActualBehavior] = useState('');
    const [additionalContext, setAdditionalContext] = useState('');

    // Generate system information
    const getSystemInfo = () => {

        const userAgent = navigator.userAgent;
        
        return `**System Information**

- Browser: ${userAgent}
- Language: ${currentLocale || 'en'}
- Selected Database: ${selectedDatabase || i18n.t('bugReportNoMethod')}
- File ID: ${currentFileId || i18n.t('bugReportNoFileId')}
- Job ID: ${currentJobId || i18n.t('bugReportNoJobId')}
- Annotation method: ${lastAnnotationMethod || i18n.t('bugReportNoMethod')}
- Screen resolution: ${window.screen.width}x${window.screen.height}
- Viewport size: ${window.innerWidth}x${window.innerHeight}`;
    };

    // Generate full issue template
    const getIssueTemplate = () => {
        let template = `## Bug Report\n\n`;
        
        // Only include sections if user has entered text
        if (actualBehavior && actualBehavior.trim()) {
            template += `**Describe the bug**\n${actualBehavior.trim()}\n\n`;
        }
        
        if (stepsToReproduce && stepsToReproduce.trim()) {
            template += `**To Reproduce**\nSteps to reproduce the behavior:\n${stepsToReproduce.trim()}\n\n`;
        }
        
        if (expectedBehavior && expectedBehavior.trim()) {
            template += `**Expected behavior**\n${expectedBehavior.trim()}\n\n`;
        }
        
        if (additionalContext && additionalContext.trim()) {
            template += `**Additional context**\n${additionalContext.trim()}\n\n`;
        }
        
        // Always include system information
        template += getSystemInfo();
        
        return template;
    };

    // Copy full template to clipboard
    const copyTemplate = async () => {
        try {
            await navigator.clipboard.writeText(getIssueTemplate());
            message.success('Issue template copied to clipboard!');
        } catch (err) {
            message.error('Failed to copy to clipboard');
        }
    };

    // Open GitHub issues page
    const openGitHub = () => {
        window.open(i18n.t('bugReportGitHubLink'), '_blank');
    };

    return (
        <div style={{ padding: '24px', maxWidth: '1200px', margin: '0 auto' }}>
            <Row gutter={[24, 24]}>
                <Col span={24}>
                    <Row align="middle" style={{ marginBottom: '24px' }}>
                        <Col>
                            <BackButton />
                        </Col>
                        <Col flex="auto" style={{ paddingLeft: '16px' }}>
                            <Title level={2} style={{ margin: 0, color: '#000000' }}>
                                {i18n.t('bugReportTitle')}
                            </Title>
                        </Col>
                    </Row>
                    
                    <Paragraph style={{ fontSize: '16px', color: 'var(--text-color-2)' }}>
                        {i18n.t('bugReportIntro')}
                    </Paragraph>
                </Col>

                {/* GitHub Repository Section */}
                <Col xs={24} lg={12}>
                    <Card 
                        title={
                            <Space>
                                <GithubOutlined />
                                {i18n.t('bugReportGitHubTitle')}
                            </Space>
                        }
                        style={{ height: '100%' }}
                    >
                        <Paragraph>{i18n.t('bugReportGitHubText')}</Paragraph>
                        <Button 
                            type="primary" 
                            icon={<GithubOutlined />}
                            onClick={openGitHub}
                            size="large"
                            style={{ width: '100%' }}
                        >
                            Open GitHub Issues
                        </Button>
                    </Card>
                </Col>

                {/* System Information Section */}
                <Col xs={24} lg={12}>
                    <Card 
                        title={i18n.t('bugReportSystemInfoTitle')}
                        style={{ height: '100%' }}
                    >
                        <div 
                            style={{ 
                                backgroundColor: '#f5f5f5',
                                padding: '12px',
                                borderRadius: '6px',
                                fontFamily: 'monospace',
                                fontSize: '12px',
                                whiteSpace: 'pre-line',
                                maxHeight: '200px',
                                overflow: 'auto',
                                border: '1px solid #d9d9d9',
                                textAlign: 'left'
                            }}
                        >
                            {getSystemInfo()}
                        </div>
                    </Card>
                </Col>

                {/* Issue Template Section */}
                <Col span={24}>
                    <Card 
                        title={i18n.t('bugReportTemplateTitle')}
                    >
                        <Paragraph style={{ marginBottom: '24px' }}>
                            {i18n.t('bugReportTemplateDesc')}
                        </Paragraph>

                        <Row gutter={[16, 16]}>
                            <Col xs={24} md={12}>
                                <Space direction="vertical" style={{ width: '100%' }} size="large">
                                    <div>
                                        <Text strong>{i18n.t('bugReportStepsTitle')}</Text>
                                        <TextArea
                                            rows={4}
                                            placeholder={i18n.t('bugReportStepsPlaceholder')}
                                            value={stepsToReproduce}
                                            onChange={(e) => setStepsToReproduce(e.target.value)}
                                            style={{ marginTop: '8px' }}
                                        />
                                    </div>

                                    <div>
                                        <Text strong>{i18n.t('bugReportExpectedTitle')}</Text>
                                        <TextArea
                                            rows={4}
                                            placeholder={i18n.t('bugReportExpectedPlaceholder')}
                                            value={expectedBehavior}
                                            onChange={(e) => setExpectedBehavior(e.target.value)}
                                            style={{ marginTop: '8px' }}
                                        />
                                    </div>
                                </Space>
                            </Col>

                            <Col xs={24} md={12}>
                                <Space direction="vertical" style={{ width: '100%' }} size="large">
                                    <div>
                                        <Text strong>{i18n.t('bugReportActualTitle')}</Text>
                                        <TextArea
                                            rows={4}
                                            placeholder={i18n.t('bugReportActualPlaceholder')}
                                            value={actualBehavior}
                                            onChange={(e) => setActualBehavior(e.target.value)}
                                            style={{ marginTop: '8px' }}
                                        />
                                    </div>

                                    <div>
                                        <Text strong>{i18n.t('bugReportAdditionalTitle')}</Text>
                                        <TextArea
                                            rows={4}
                                            placeholder={i18n.t('bugReportAdditionalPlaceholder')}
                                            value={additionalContext}
                                            onChange={(e) => setAdditionalContext(e.target.value)}
                                            style={{ marginTop: '8px' }}
                                        />
                                    </div>
                                </Space>
                            </Col>
                        </Row>

                        <Divider />

                        <div>
                            <Row justify="space-between" align="middle" style={{ marginBottom: '8px' }}>
                                <Col>
                                    <Text strong>{i18n.t('bugReportReportHeader')}:</Text>
                                </Col>
                                <Col>
                                    <Button 
                                        icon={<CopyOutlined />}
                                        onClick={copyTemplate}
                                        type="primary"
                                        size="small"
                                    >
                                        {i18n.t('bugReportCopyButton')}
                                    </Button>
                                </Col>
                            </Row>
                            <div 
                                style={{ 
                                    backgroundColor: '#f5f5f5',
                                    padding: '16px',
                                    borderRadius: '6px',
                                    fontFamily: 'monospace',
                                    fontSize: '12px',
                                    whiteSpace: 'pre-line',
                                    maxHeight: '300px',
                                    overflow: 'auto',
                                    border: '1px solid #d9d9d9',
                                    textAlign: 'left'
                                }}
                            >
                                {getIssueTemplate()}
                            </div>
                        </div>
                    </Card>
                </Col>
            </Row>
        </div>
    );
}

export default BugReportPage;
